import classNames from 'classnames';
import { type InputHTMLAttributes, useEffect, useId, useState } from 'react';

import { Label } from '../label/Label';
import styles from './Radio.module.css';
import { RadioIndicator } from './RadioIndicator';

export enum RadioLabelSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string;
  size?: RadioLabelSize;
  'data-testid'?: string;
}

export const Radio = ({
  label,
  disabled,
  className,
  size = RadioLabelSize.MD,
  style,
  id,

  checked,
  defaultChecked,
  onChange,
  onFocus,
  onBlur,

  'data-testid': dataTestId,
  ...inputProps
}: RadioProps) => {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const isControlled = checked !== undefined;

  // Internal state for uncontrolled mode
  const [internalChecked, setInternalChecked] = useState<boolean>(
    defaultChecked ?? false
  );

  // If controlled value changes, sync internal state (so indicator stays correct)
  useEffect(() => {
    if (isControlled) {
      setInternalChecked(!!checked);
    }
  }, [checked, isControlled]);

  const isChecked = isControlled ? !!checked : internalChecked;

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <label
      className={classNames(
        styles.container,
        disabled && styles.disabled,
        className
      )}
      style={style}
      htmlFor={disabled ? undefined : controlId}
      data-testid={dataTestId}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        {...inputProps}
        type="radio"
        id={controlId}
        className={styles.input}
        disabled={disabled}
        checked={isControlled ? checked : internalChecked}
        onChange={(e) => {
          if (!isControlled) {
            setInternalChecked(e.target.checked);
          }
          onChange?.(e);
        }}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
      />

      <RadioIndicator
        checked={isChecked}
        disabled={disabled}
        isHovered={isHovered}
        isFocused={isFocused}
      />

      {label && (
        <Label as="span" disabled={disabled} size={size}>
          {label}
        </Label>
      )}
    </label>
  );
};
